import { jsx as _jsx } from "react/jsx-runtime";
/**
 * DensityProvider — controls spacing density across the UI.
 *
 * Three density levels: compact (data-dense views), default, comfortable (touch-friendly).
 * Components call `useDensity()` to read the current DensityConfig.
 *
 * Finding H-01: NO wrapper div / display:contents. We set CSS custom properties
 * directly on documentElement so there is zero DOM overhead, and components
 * consume the config via React Context (no CSS variable round-trip needed).
 */
import { createContext, useContext, useEffect, useMemo } from 'react';
const DENSITY_CONFIGS = {
    compact: {
        density: 'compact',
        padding: '8px',
        paddingLg: '12px',
        gap: '4px',
        gapLg: '8px',
        rowHeight: '36px',
        fontBody: '13px',
        fontLabel: '11px',
        radius: '6px',
    },
    default: {
        density: 'default',
        padding: '12px',
        paddingLg: '16px',
        gap: '8px',
        gapLg: '12px',
        rowHeight: '44px',
        fontBody: '14px',
        fontLabel: '12px',
        radius: '10px',
    },
    comfortable: {
        density: 'comfortable',
        padding: '16px',
        paddingLg: '24px',
        gap: '12px',
        gapLg: '16px',
        rowHeight: '56px',
        fontBody: '16px',
        fontLabel: '13px',
        radius: '14px',
    },
};
const DensityContext = createContext(DENSITY_CONFIGS.default);
/**
 * Get a density config by name (for use outside React tree).
 */
export function getDensityConfig(density) {
    return DENSITY_CONFIGS[density];
}
/**
 * Provides density values via React Context and mirrors them onto
 * `document.documentElement` as CSS custom properties so plain CSS
 * can also consume them (e.g., scrollbar sizing, focus outlines).
 */
export function DensityProvider({ density, children }) {
    const config = useMemo(() => DENSITY_CONFIGS[density], [density]);
    // Mirror to CSS custom properties on <html> — no wrapper div (H-01)
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        const root = document.documentElement;
        root.style.setProperty('--k-density-padding', config.padding);
        root.style.setProperty('--k-density-padding-lg', config.paddingLg);
        root.style.setProperty('--k-density-gap', config.gap);
        root.style.setProperty('--k-density-gap-lg', config.gapLg);
        root.style.setProperty('--k-density-row-height', config.rowHeight);
        root.style.setProperty('--k-density-font-body', config.fontBody);
        root.style.setProperty('--k-density-font-label', config.fontLabel);
        root.style.setProperty('--k-density-radius', config.radius);
    }, [config]);
    return _jsx(DensityContext.Provider, { value: config, children: children });
}
/**
 * Read the current density config from context.
 */
export function useDensity() {
    return useContext(DensityContext);
}
//# sourceMappingURL=DensityProvider.js.map